/**
 * The label this prototype runs on: payees to add to a contract and a small
 * catalogue to pick its tracks from.
 *
 * Scaffolding — delete with the rest of src/prototypes/modular-contract-builder/.
 */
import { DEV_LABEL } from "@/lib/dev-mode";
import type { ContractExtras } from "./store";

const now = "2026-01-15T10:00:00Z";

export const LABEL = { ...DEV_LABEL, payment_threshold: "50.00" };

export const PAYEES = [
  {
    id: "payee-nova-skye",
    name: "Nova Skye",
    legal_name: "Skyeline Music Ltd",
    country: "GB",
    preferred_currency: "USD",
  },
  {
    id: "payee-marcus-vale",
    name: "Marcus Vale",
    legal_name: "Vale Recordings LLC",
    country: "US",
    preferred_currency: "USD",
  },
  {
    id: "payee-hollow-pines",
    name: "The Hollow Pines",
    legal_name: "Hollow Pines Collective",
    country: "CA",
    preferred_currency: "USD",
  },
  {
    id: "payee-june-ashworth",
    name: "June Ashworth",
    legal_name: "J. Ashworth Productions",
    country: "GB",
    preferred_currency: "GBP",
  },
  // A long name, to see how the split tables take it.
  {
    id: "payee-aurelia-montgomery",
    name: "Aurelia Montgomery-Fitzgerald & The Midnight Harbour Orchestra",
    legal_name: "Montgomery-Fitzgerald Music Publishing International Ltd",
    country: "IE",
    preferred_currency: "EUR",
  },
].map((payee) => ({
  ...payee,
  contact_email: `${payee.name.toLowerCase().replace(/[^a-z]+/g, ".")}@example.com`,
  city: "",
  is_active: true,
  label: LABEL.id,
  label_name: LABEL.name,
  created_at: now,
  updated_at: now,
  can_delete: false,
}));

export const RELEASES = [
  {
    id: "rel-neon-harbour",
    title: "Neon Harbour",
    type: "album",
    release_date: "2025-03-14",
    catalog_number: "TRK012",
    upc_code: "196925401127",
  },
  {
    id: "rel-paper-lanterns",
    title: "Paper Lanterns",
    type: "ep",
    release_date: "2025-06-06",
    catalog_number: "TRK015",
    upc_code: "196925401158",
  },
  {
    id: "rel-slow-tide",
    title: "Slow Tide",
    type: "single",
    release_date: "2025-09-12",
    catalog_number: "TRK018",
    upc_code: "196925401189",
  },
  // A compilation: tracks first out on other releases, each now on two.
  {
    id: "rel-late-night-selections",
    title: "Late Night Selections",
    type: "compilation",
    release_date: "2025-11-07",
    catalog_number: "TRK020",
    upc_code: "196925401202",
    artist: "Various Artists",
  },
  // Not on any contract yet: the uploaded contract's tracks, free to pick.
  {
    id: "rel-afterglow",
    title: "Afterglow",
    type: "album",
    release_date: "2026-02-20",
    catalog_number: "TRK022",
    upc_code: "196925401226",
  },
  // Demo releases, on no contract: more tracks to pick from.
  {
    id: "rel-copper-sky",
    title: "Copper Sky",
    type: "ep",
    release_date: "2026-04-10",
    catalog_number: "TRK024",
    upc_code: "196925401240",
  },
  {
    id: "rel-northern-lines",
    title: "Northern Lines",
    type: "album",
    release_date: "2026-05-22",
    catalog_number: "TRK026",
    upc_code: "196925401264",
  },
];

export const RECORDINGS = [
  {
    id: "rec-midnight-signal",
    title: "Midnight Signal",
    mix_name: "Original Mix",
    isrc: "GBTRK2500001",
    release: "rel-neon-harbour",
    track_number: "1",
    artist: "Nova Skye",
  },
  {
    id: "rec-harbour-lights",
    title: "Harbour Lights",
    mix_name: "Album Version",
    isrc: "GBTRK2500002",
    release: "rel-neon-harbour",
    // Also on the compilation: a track on two releases.
    also_on: [{ release: "rel-late-night-selections", track_number: "1" }],
    track_number: "2",
    artist: "Nova Skye",
  },
  {
    id: "rec-glass-avenue",
    title: "Glass Avenue",
    mix_name: "Radio Edit",
    isrc: "GBTRK2500003",
    release: "rel-neon-harbour",
    track_number: "3",
    artist: "Nova Skye",
  },
  {
    id: "rec-undertow",
    title: "Undertow",
    mix_name: "Club Mix",
    isrc: "GBTRK2500007",
    release: "rel-neon-harbour",
    track_number: "4",
    artist: "Nova Skye",
  },
  {
    id: "rec-salt-lines",
    title: "Salt Lines",
    mix_name: "Acoustic",
    isrc: "GBTRK2500008",
    release: "rel-paper-lanterns",
    track_number: "3",
    artist: "Marcus Vale",
  },
  {
    id: "rec-paper-lanterns",
    title: "Paper Lanterns",
    mix_name: "Single Version",
    isrc: "USTRK2500004",
    release: "rel-paper-lanterns",
    track_number: "1",
    artist: "Marcus Vale",
  },
  {
    id: "rec-northbound",
    title: "Northbound",
    mix_name: "Live",
    isrc: "USTRK2500005",
    release: "rel-paper-lanterns",
    also_on: [{ release: "rel-late-night-selections", track_number: "2" }],
    track_number: "2",
    artist: "Marcus Vale",
  },
  {
    id: "rec-slow-tide",
    title: "Slow Tide",
    mix_name: "Remastered",
    isrc: "CATRK2500006",
    release: "rel-slow-tide",
    track_number: "1",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-static-bloom",
    title: "Static Bloom",
    mix_name: "Instrumental",
    isrc: "GBTRK2600009",
    release: "rel-afterglow",
    track_number: "1",
    artist: "Nova Skye",
  },
  {
    id: "rec-low-orbit",
    title: "Low Orbit",
    mix_name: "Dub Mix",
    isrc: "GBTRK2600010",
    release: "rel-afterglow",
    track_number: "2",
    artist: "Nova Skye",
  },
  {
    id: "rec-coastline",
    title: "Coastline",
    mix_name: "Piano Version",
    isrc: "GBTRK2600011",
    release: "rel-afterglow",
    track_number: "3",
    artist: "Nova Skye",
  },
  {
    id: "rec-after-hours",
    title: "After Hours",
    mix_name: "Extended Mix",
    isrc: "GBTRK2600012",
    release: "rel-afterglow",
    track_number: "4",
    artist: "Nova Skye",
  },
  // The demo releases' tracks: Copper Sky's, then Northern Lines'.
  {
    id: "rec-copper-sky",
    title: "Copper Sky",
    mix_name: "Original Mix",
    isrc: "GBTRK2600013",
    release: "rel-copper-sky",
    track_number: "1",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-quiet-engines",
    title: "Quiet Engines",
    mix_name: "Radio Edit",
    isrc: "GBTRK2600014",
    release: "rel-copper-sky",
    track_number: "2",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-dust-and-chrome",
    title: "Dust and Chrome",
    mix_name: "Album Version",
    isrc: "GBTRK2600015",
    release: "rel-copper-sky",
    track_number: "3",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-signal-fire",
    title: "Signal Fire",
    mix_name: "Live Session",
    isrc: "GBTRK2600016",
    release: "rel-copper-sky",
    track_number: "4",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-long-way-home",
    title: "Long Way Home",
    mix_name: "Acoustic",
    isrc: "GBTRK2600017",
    release: "rel-copper-sky",
    track_number: "5",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-northern-lines",
    title: "Northern Lines",
    mix_name: "Original Mix",
    isrc: "GBTRK2600018",
    release: "rel-northern-lines",
    track_number: "1",
    artist: "June Ashworth",
  },
  {
    id: "rec-paper-planes",
    title: "Paper Planes",
    mix_name: "Single Edit",
    isrc: "GBTRK2600019",
    release: "rel-northern-lines",
    track_number: "2",
    artist: "June Ashworth",
  },
  {
    id: "rec-cold-light",
    title: "Cold Light",
    mix_name: "Album Version",
    isrc: "GBTRK2600020",
    release: "rel-northern-lines",
    track_number: "3",
    artist: "June Ashworth",
  },
  {
    id: "rec-harbour-wall",
    title: "Harbour Wall",
    mix_name: "Demo",
    isrc: "GBTRK2600021",
    release: "rel-northern-lines",
    track_number: "4",
    artist: "June Ashworth",
  },
  {
    id: "rec-last-ferry",
    title: "Last Ferry",
    mix_name: "Reprise",
    isrc: "GBTRK2600022",
    release: "rel-northern-lines",
    track_number: "5",
    artist: "June Ashworth",
  },
];

/** The releases a track is on, with its number on each: the one it came out on, then any others (a compilation). */
export function releasesOf(
  recording: (typeof RECORDINGS)[number],
): { release: string; track_number: string }[] {
  return [
    { release: recording.release, track_number: recording.track_number },
    ...(("also_on" in recording && recording.also_on) || []),
  ];
}

/** The releases a track is on: the one it came out on, then any others (a compilation). */
export function releaseIdsOf(recording: (typeof RECORDINGS)[number]): string[] {
  return releasesOf(recording).map((r) => r.release);
}

/** The tracks on a release, the ones also on it included. */
export function recordingsOn(releaseId: string) {
  return RECORDINGS.filter((r) => releaseIdsOf(r).includes(releaseId));
}

/** Contracts already on the label — what a sub-contract is made under. */
export const CONTRACTS = [
  {
    id: "contract-nova-skye",
    name: "Nova Skye — Contract A",
    contract_type: "recording",
    status: "active",
    start_date: "2024-11-01",
    end_date: "2027-10-31",
    label_share_percent: "50.00",
    payees: [{ payee: "payee-nova-skye", share: 100 }],
    tracks: ["rec-midnight-signal", "rec-harbour-lights", "rec-glass-avenue", "rec-undertow"],
  },
  {
    id: "contract-marcus-vale",
    name: "Marcus Vale — Recording Agreement",
    contract_type: "recording",
    status: "active",
    start_date: "2025-01-01",
    end_date: "2028-12-31",
    label_share_percent: "60.00",
    payees: [
      { payee: "payee-marcus-vale", share: 80 },
      { payee: "payee-june-ashworth", share: 20 },
    ],
    tracks: ["rec-paper-lanterns", "rec-northbound"],
  },
  {
    id: "contract-hollow-pines",
    name: "The Hollow Pines — Distribution Deal",
    contract_type: "distribution",
    status: "active",
    start_date: "2024-05-01",
    end_date: null,
    label_share_percent: "30.00",
    payees: [{ payee: "payee-hollow-pines", share: 100 }],
    tracks: ["rec-slow-tide"],
  },
];

/**
 * What the sample contracts hold beyond the API's fields, as the builder keeps
 * it: Contract A has the scenario's Vinyl split — the label 70%, Nova Skye 30%.
 */
export const CONTRACT_EXTRAS: Record<string, ContractExtras> = {
  "contract-nova-skye": {
    formatRules: [
      {
        key: "rule-contract-a-vinyl",
        format: "vinyl",
        labelShare: "70",
        shares: { "payee-nova-skye": "30" },
      },
    ],
    advances: {},
  },
};

/**
 * What "processing" an uploaded contract PDF finds, whatever the file: the
 * scenario card's deal. The label and Nova Skye split 50/50, except vinyl
 * (label 70%, Nova Skye 30%). Nova Skye is advanced $5,000, half of it
 * recoupable, and the contract covers the 4 tracks of Afterglow — on no other
 * contract yet (Neon Harbour's are Contract A's). Expenses
 * are split as revenue. The contract's name is the file's.
 */
export const EXTRACTED_CONTRACT = {
  fields: {
    contract_type: "recording",
    status: "draft",
    start_date: "2026-01-01",
    end_date: null,
    territory: "worldwide",
    currency: "USD",
    statement_period: "half_yearly",
    payment_threshold: "50.00",
    label_share_percent: "50.00",
    advance_amount: "5000.00",
    // As the API keeps it: each payee's part of what's left after the label.
    payees: [{ payee: "payee-nova-skye", share_percentage: 100 }],
  },
  tracks: ["rec-static-bloom", "rec-low-orbit", "rec-coastline", "rec-after-hours"],
  extras: {
    formatRules: [
      {
        key: "rule-extracted-vinyl",
        format: "vinyl",
        labelShare: "70",
        shares: { "payee-nova-skye": "30" },
      },
    ],
    advances: { "payee-nova-skye": { amount: "5000", recoupable: "50", paidOn: null } },
    advancePayees: [{ id: "payee-nova-skye", name: "Nova Skye" }],
    expenseSplit: { labelShare: "50", shares: { "payee-nova-skye": "50" } },
  } satisfies ContractExtras,
};
